import { Filter, FilterModel, RelatedTag } from '@empathy/search-types';
import { EmpathyXConfig, URLDefaultMappings, URLStoreMode } from '../../../models';
import { GETTERS_KEYS } from '../../store.const';
import { URL_ACTIONS_KEYS, URL_GETTERS_KEYS, URL_MUTATIONS_KEYS } from '../url.const';
import { URLAction, URLContext } from '../url.interfaces';

export class StoreURLAction implements URLAction {

  actions = {
    [URL_ACTIONS_KEYS.storeURL]: (context: URLContext, mode: URLStoreMode = URLStoreMode.NEW): any => {
      this.clearSearchQueryParameters(context);
      this.addSearchParameters(context);
      this.storeURLInHistory(context, mode);
    }
  };

  protected clearSearchQueryParameters(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    Object.values(queryDefaultMappings).forEach(queryParam => searchParams.delete(queryParam));
    return searchParams;
  }

  protected addSearchParameters(context: URLContext): URLSearchParams {
    const { searchParams } = this.getURLParamsAndMappings(context);
    if (context.rootState.search.query) {
      this.addQueryParameter(context);
      this.addFilterParameter(context);
      this.addPageParameter(context);
      this.addFirstGridItemIdInViewParameter(context);
      this.addSortParameter(context);
      this.addRelatedTagsParameter(context);
    }
    return searchParams;
  }

  protected storeURLInHistory({ commit, getters, rootGetters }: URLContext, mode: URLStoreMode) {
    const { url: { keepURLEncoding } }: EmpathyXConfig = rootGetters[GETTERS_KEYS.config.config];
    const url: URL = getters[URL_GETTERS_KEYS.url];
    const href = keepURLEncoding ? decodeURIComponent(url.href) : url.href;
    if (!history.state || href !== history.state.href) {
      if (mode === URLStoreMode.REPLACE) {
        history.replaceState({ href }, document.title, href);
      } else {
        history.pushState({ href }, document.title, href);
      }
      commit(URL_MUTATIONS_KEYS.setURL, href);
    }
  }

  protected getURLParamsAndMappings(context: URLContext): { searchParams: URLSearchParams, queryDefaultMappings: URLDefaultMappings } {
    return {
      searchParams: context.getters[URL_GETTERS_KEYS.url].searchParams,
      queryDefaultMappings: context.rootGetters[GETTERS_KEYS.config.url].queryDefaultMappings
    };
  }

  protected addQueryParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    searchParams.set(queryDefaultMappings.query, context.rootState.search.query);
    return searchParams;
  }

  protected addFilterParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const selectedFilters: Filter[] = context.rootGetters[GETTERS_KEYS.search.selectedFilters];
    selectedFilters.forEach((selectedFilter: Filter) => {
      if (selectedFilter.modelName === FilterModel.currencyRange) {
        const selectedFilterModel = selectedFilter.facet.modelName;
        const selectedFilterValue = selectedFilter.value;
        selectedFilter.id = `${selectedFilterModel}:${selectedFilterValue.min} TO ${selectedFilterValue.max}`;
      }
      searchParams.append(queryDefaultMappings.filters, selectedFilter.id);
    });
    return searchParams;
  }

  protected addPageParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const page = context.rootState.search.page;
    if (page > 1) {
      searchParams.set(queryDefaultMappings.page, page.toString());
    }
    return searchParams;
  }

  protected addFirstGridItemIdInViewParameter(context: URLContext): URLSearchParams {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const firstGridItemIdInView = context.rootState.core.firstGridItemIdInView;
    if (firstGridItemIdInView) {
      searchParams.set(queryDefaultMappings.firstGridItemIdInView, firstGridItemIdInView);
    }
    return searchParams;
  }

  protected addSortParameter(context: URLContext) {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const sortValue = context.rootState.search.sort.value;
    if (sortValue) {
      searchParams.set(queryDefaultMappings.sort, sortValue);
    }
  }

  protected addRelatedTagsParameter(context: URLContext) {
    const { searchParams, queryDefaultMappings } = this.getURLParamsAndMappings(context);
    const selectedRelatedTags: RelatedTag[] = context.rootGetters[GETTERS_KEYS.querySignals.selectedRelatedTags];
    selectedRelatedTags.forEach(relatedTag => {
      searchParams.append(queryDefaultMappings.relatedTags, relatedTag.tag);
    });
  }
}
